import { getSession } from "@anchor/anchor-auth/react";
import { jwtDecode } from "jwt-decode";

interface JwtPayload {
  exp: number;
  nbf: number;
  iat: number;
  roles?: string[];
  name?: string;
  preferred_username?: string;
  [key: string]: any;
}

/**
 * Gets token from localStorage by name
 */
export const getToken = (name = "accessToken"): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const token = localStorage.getItem(name);
  const expiresOn = localStorage.getItem(`${name}_expiresOn`);

  if (token && expiresOn) {
    const expiryDate = new Date(expiresOn);
    // Check if the date is valid and expired
    if (!isNaN(expiryDate.getTime()) && expiryDate <= new Date()) {
      // Token expired, clear it
      localStorage.removeItem(name);
      localStorage.removeItem(`${name}_expiresOn`);
      return null;
    }
  }

  return token;
};

export const clearToken = (name = "accessToken"): void => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(name);
  localStorage.removeItem(`${name}_expiresOn`);
};

/**
 * Decodes JWT token and returns the payload
 */
export const decodeToken = (token: string): JwtPayload | null => {
  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
};

/**
 * Gets user roles from token
 */
export const getRolesFromToken = (token: string): string[] => {
  const decoded = decodeToken(token);
  return decoded?.roles || [];
};

/**
 * Checks if the user has any of the required authorities
 */
export const hasAuthority = (
  token: string | null,
  authorities: string[],
  skipAuth = false
): boolean => {
  if (skipAuth) return true;
  if (!token) return false;

  const roles = getRolesFromToken(token);
  return roles.some((role) => authorities.includes(role));
};

/**
 * Complete authentication check from cookie or sessionStorage
 */
export const checkAuthentication = (): {
  authenticated: boolean;
  token: string | null;
  roles: string[];
  username?: string;
} => {
  // Try local storage first
  const token = getToken();

  if (!token) {
    return {
      authenticated: false,
      token: null,
      roles: [],
    };
  }

  const decoded = decodeToken(token);

  return {
    authenticated: true,
    token,
    roles: decoded?.roles || [],
    username: decoded?.name || decoded?.preferred_username,
  };
};

// SSE requests read the token from localStorage, which is only rewritten
// when the session refetches. A background tab can outlive that token, so pull
// a fresh one from the session before reconnecting after a 401.
export const refreshStoredToken = async () => {
  try {
    const session = await getSession();
    const details = (session as { apiTokenDetails?: { accessToken?: string; expiresOn?: string } })
      ?.apiTokenDetails;
    if (!details?.accessToken) return;
    localStorage.setItem("accessToken", details.accessToken);
    if (details.expiresOn) localStorage.setItem("accessToken_expiresOn", details.expiresOn);
  } catch (error) {
    console.error("Failed to refresh token for SSE:", error);
  }
};
